import React, { useEffect, useMemo, useRef, useState } from 'react';
import { resampleSpectrum, spectrumBandCountForWidth } from './voice-spectrum';

interface VoiceWaveformProps {
  inputLevel: number;
  outputLevel: number;
  inputSpectrum: readonly number[] | null;
  outputSpectrum: readonly number[] | null;
}

const clampLevel = (level: number): number => Math.min(1, Math.max(0, level));

const AccessibleMeter: React.FC<{ label: string; level: number }> = ({
  label,
  level,
}) => {
  const percent = Math.round(clampLevel(level) * 100);
  return (
    <span
      className="voice-spectrum-meter"
      role="meter"
      aria-label={`${label} level`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-valuetext={`${percent} percent`}
    />
  );
};

const VoiceWaveform: React.FC<VoiceWaveformProps> = ({
  inputLevel,
  outputLevel,
  inputSpectrum,
  outputSpectrum,
}) => {
  const plotRef = useRef<HTMLDivElement>(null);
  const [bandCount, setBandCount] = useState(() =>
    spectrumBandCountForWidth(0),
  );

  useEffect(() => {
    const plot = plotRef.current;
    if (!plot || typeof ResizeObserver === 'undefined') return;
    const updateWidth = (width: number): void => {
      setBandCount(spectrumBandCountForWidth(width));
    };
    updateWidth(plot.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const entry = entries.find(({ target }) => target === plot);
      if (entry) updateWidth(entry.contentRect.width);
    });
    observer.observe(plot);
    return () => observer.disconnect();
  }, []);

  const micBands = useMemo(
    () => resampleSpectrum(inputSpectrum, bandCount, inputLevel),
    [bandCount, inputLevel, inputSpectrum],
  );
  const agentBands = useMemo(
    () => resampleSpectrum(outputSpectrum, bandCount, outputLevel),
    [bandCount, outputLevel, outputSpectrum],
  );

  return (
    <div className="voice-waveform" aria-label="Live audio spectrum">
      <div
        className="voice-spectrum-plot"
        ref={plotRef}
        aria-hidden="true"
        data-band-count={bandCount}>
        {micBands.map((micLevel, index) => (
          <span className="voice-spectrum-pair" key={index}>
            <i
              className="voice-spectrum-bar voice-spectrum-bar-input"
              data-signal="mic"
              style={{ height: `${micLevel * 100}%`, width: '3px' }}
            />
            <i
              className="voice-spectrum-bar voice-spectrum-bar-output"
              data-signal="agent"
              style={{ height: `${agentBands[index] * 100}%`, width: '3px' }}
            />
          </span>
        ))}
      </div>
      <AccessibleMeter label="Mic input" level={inputLevel} />
      <AccessibleMeter label="Agent output" level={outputLevel} />
    </div>
  );
};

export default VoiceWaveform;
